/**
 * All of the forms that support the different Custmer actions
 * are listed here
 * 
 * snippets/login-form.liquid
 * snippets/register-form.liquid
 * sections/account-user-reset.liquid
 * sections/account-password-reset.liquid
 * snippets/change-password-form.liquid
 */

/**
 * Stores the username key both as a Cart attribute and also
 * in localStorage for easy access from Liquid and JavaScript
 * 
 * @param {string} key - The username key
 */
const storeUsernameKey = async (key) => {
  const formData = new FormData();

  formData.append('attributes[username_key]', key);
  formData.append('attributes[login_refresh]', 'true');
  const headers = window.getCartHeaders();
  delete headers['Content-Type']; // Remove Content-Type for FormData
  
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: headers,
    body: formData,
  });

  if(response.ok){
    // Dispatch cart updated event
    const responseJson = await response.json();
  }else{
    throw new Error('Error storing Username key');
  }
}

class CustomerForm extends HTMLElement {
  static usernameFieldNames = ['username', 'customer_username'];
  static passwordFieldNames = ['password', 'customer_password'];

  constructor(name) {
    super();

    this.enableLog = true;
    this.name = name || 'CustomerForm';
    this._loading = false;
  }

  connectedCallback() {
    this.log('Initialising', this, { ...this });
    this.config = this.getConfig();
    this.log('Configuration:', this.config);
    this.elems = /** @type {ReturnType<typeof this.getElems>} */ (this.getElems());
    this.log('Elements:', this.elems);

    if ('handleSubmit' in this) {
      this.elems.form?.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (this.loading) return;

        try {
          this.clearMessages();
          this.loading = true;

          this.validateDefaultFields();

          const { successMessage, errorMessage, systemErrorMessage } = await this.handleSubmit(event);

          if (errorMessage) this.showMessage(errorMessage);
          if (systemErrorMessage) throw new Error(systemErrorMessage);
          if(successMessage && successMessage != ''){
            this.showMessage(successMessage, 'success');
          }
        } catch (error) {
          let errorMessage = 'Unknown error';
          if (error instanceof Error) {
            errorMessage = error.message;
          }

          this.log('Error submitting form', errorMessage);
          this.loading = false;

          const messageEl = this.querySelector('.js-customer-form-message');
          if (messageEl === null) {
            this.showMessage('Es ist ein Fehler aufgetreten');
          }
        }
      });
    } else {
      this.log('No submit handler defined');
    }

    this.initInputFields();
  }

  initInputFields() {
    [...this.elems.form.querySelectorAll('input')].forEach(inputEl => {
      inputEl.addEventListener('blur', (event) => {
        event.currentTarget.classList.remove('input--error');
      });
    });
  }

  async storeUsernameKeyAndLogin(usernameKey, token) {
    /**
     * We store the username key as we will need to
     * retrieve this after succesfully logging in with Multipass
     */
    await storeUsernameKey(usernameKey);

    localStorage.setItem('logged_in', 'true');

    /**
     * Redirect to multipass with the token
     */
    //window.location.href = `${location.origin}/account/login/multipass/${token}?return_to=/pages/shop`;
    
    //Due to new login system we have commented above code - 28-04-2026
    window.location.href = `/customer_authentication/login?return_to=%2Fpages%2Fshop`;
  }

  showMessage(message, type = 'error') {
    if (this.elems?.submitButton !== null) {
      const className = (() => {
        switch (type) {
          case 'success':
            return 'bg-success-bg text-success-text';
          case 'error':
          default:
            return 'bg-error-bg text-error-text'
        }
      })();

      const html = `
        <div class="alert reading-width mb-8 mx-auto ${className} js-customer-form-message" tabindex="-1" autofocus>
          ${message}
        </div>
      `;

      this.elems.submitButton.parentNode.insertAdjacentHTML('beforeBegin', html);
    }
  }

  clearMessages() {
    const messageEl = this.querySelector('.js-customer-form-message');
    messageEl?.remove();
  }

  showFieldError(name) {
    this.elems.form.elements[name].classList.add('input--error');
  }

  validateDefaultFields() {
    const entries = Object.fromEntries(new FormData(this.elems.form));

    const customerValidations = CustomerForm.usernameFieldNames.map(fieldName => {
      if (fieldName in entries) {
        const username = entries[fieldName];
        const isValidUsername = this.validateUsername(username);
        if (isValidUsername) return null;

        return {
          name: fieldName,
          errorMessage: 'Bitte keine Leer-, Sonderzeichen und im Benutzernamen verwenden. Ausnahmen sind: @ . _ - & $ ! ( ) + / ='
        };
      }

      return null;
    });

    const passwordValidations = CustomerForm.passwordFieldNames.map(fieldName => {
      if (fieldName in entries) {
        const password = entries[fieldName];
        const isValidPassword = this.validatePassword(password);
        if (isValidPassword) return null;

        return {
          name: fieldName,
          errorMessage: 'Passwort muss aus mind. 6 Zeichen bestehen.'
        };
      }

      return null;
    });

    const errors = [...customerValidations, ...passwordValidations].filter(error => error !== null);

    if (errors.length > 0) {
      const { name, errorMessage } = errors[0];
      this.showFieldError(name);
      this.showMessage(errorMessage)
      throw new Error(`Field is ${name} invalid`);
    }
  }

  /**
   * Checks if a string is a valid email address
   * 
   * @param {string} email 
   * 
   * @returns {boolean}
   */
  validateEmail(email) {
    const regExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return regExp.test(email);
  }

  /**
   * Checks if a string is a valid password
   * 
   * @param {string} email 
   * 
   * @returns {boolean}
   */
  validatePassword(password) {
    return password && password.length >= 6;
  }

  /**
   * Checks if a string is a valid username
   * 
   * @param {string} username 
   * 
   * @returns {boolean}
   */
  validateUsername1(username) {
    return /^[a-zA-Z0-9@._-]*$/.test(username)
  }

  // validateUsername(username) {
  //   // Matches a valid character at the start, 
  //   // optional middle section with spaces, 
  //   // and a valid character at the end.
  //   const regex = /^[a-zA-Z0-9@._\-]([a-zA-Z0-9@._\- ]*[a-zA-Z0-9@._\-])?$/;
  //   return regex.test(username);
  // }
  // validateUsername(username) {
  //   // Allows letters (including umlauts), numbers, @ . _ - and spaces
  //   // Must start and end with a non-space valid character
  //   const regex = /^[\p{L}0-9@._\-]([\p{L}0-9@._\- ]*[\p{L}0-9@._\-])?$/u;
  //   return regex.test(username);
  // }

  validateUsername(username) {
    // Allows:
    // - Unicode letters (including German umlauts)
    // - Numbers
    // - Common special characters: @ . _ - & $ ! ( ) + / = ,
    // - Spaces between words
    //
    // Rules:
    // - Username cannot start or end with a space
    // - Multiple words separated by single spaces are allowed
    //
    // Examples:
    // ✅ Max Müller
    // ✅ Anna#1
    // ✅ Müller&Söhne
    // ✅ Test_User-99
    //
    // Requires the `u` flag for Unicode support (\p{L})
    const regex = /^[\p{L}0-9@._\-&$!()+/=,]+(?: [\p{L}0-9@._\-&$!()+/=,]+)*$/u;

    return regex.test(username);
  }

  get loading() {
    return this._loading;
  }

  set loading(value) {
    this._loading = value;
    const operation = value ? 'add' : 'remove';

    this.elems?.submitButton.classList[operation]('btn--loading');
  }

  getElems() {
    return {
      submitButton: /** @type {HTMLButtonElement} */ (this.querySelector('button[type="submit"]')),
      form: /** @type {HTMLFormElement} */ (this.querySelector('form'))
    }
  }

  getConfig() {
    return JSON.parse(this.querySelector('[data-config]')?.innerHTML || '{}');
  }

  log(...args) {
    if (!this.enableLog) return;
    console.log(`[${this.name}]`, ...args);
  }
};

/**
 * LOGIN FORM
 */
if (!customElements.get('form-wrapper-login')) {
  class FormWrapperLogin extends CustomerForm {
    constructor() {
      super('FormWrapperLogin');
    }

    async handleSubmit(event) {
      const { username, password } = Object.fromEntries(new FormData(event.target));

      const response = await fetch('/apps/stroetmann/api/v1/customers/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      if (!response.ok) {
        // console.log('RESPONSE NOT OK');
        const { data, error } = await response.json();
        const [errorMessage, systemErrorMessage] = ((errorMessage, systemErrorMessage) => {
          switch (error) {
            case 'error.data_invalid':
              return [this.config.strings['customer.login.error_data_invalid'], error];
            case 'error.no_access_to_any_user':
              return [this.config.strings['customer.login.error_no_auth'], 'Auth error']
            default:
              return [this.config.strings['customer.login.error'], 'Server error'];
          }
        })();
        return { errorMessage, systemErrorMessage };
      }

      const { key, token } = await response.json();
      this.storeUsernameKeyAndLogin(key, token);

      return { successMessage: this.config.strings['customer.login.success'] }
    }
  }

  customElements.define('form-wrapper-login', FormWrapperLogin);
}

/**
 * REGISTER FORM
 */
if (!customElements.get('form-wrapper-register')) {
  class FormWrapperRegister extends CustomerForm {
    static routes = {
      CREATE: '/apps/stroetmann/api/v1/customers/create',
      CREATE_WITH_NUMBER: '/apps/stroetmann/api/v1/customers/create_with_number',
    };

    static customerType = {
      NEW: 'new',
      EXISTING: 'existing'
    }

    constructor() {
      super('FormWrapperRegister');
    }

    connectedCallback() {
      super.connectedCallback();

      this.elems.customerOptions.forEach(optionEl => {
        optionEl.addEventListener('click', (event) => {
          this.handleUpdateCustomerType(event.target.dataset.customerOption);
        });
      });

      this.elems.formToggleBillingAddress.addEventListener('change', this.checkBillingAddressGroup.bind(this));
    }

    handleUpdateCustomerType(type) {
      this.log('Updating Customer type', type);

      this.elems.customerOptions.forEach(optionEl => {
        optionEl.ariaSelected = optionEl.dataset.customerOption === type;
      });

      [...this.querySelectorAll('[data-customer-context]')]
        .forEach((el) => {
          const enabled = el.getAttribute('data-customer-context') === type;

          el.disabled = !enabled;
          el.hidden = !enabled;
          el.querySelectorAll('input').forEach((input) => {
            input.disabled = !enabled;
          });
        });

      if (type === FormWrapperRegister.customerType.NEW)
        this.checkBillingAddressGroup();
    }

    checkBillingAddressGroup() {
      const checked = this.elems.formToggleBillingAddress.checked;

      if (checked) {
        this.elems.billingFields.setAttribute('hidden', '');
        this.elems.billingFields.setAttribute('disabled', '');
      } else {
        this.elems.billingFields.removeAttribute('hidden');
        this.elems.billingFields.removeAttribute('disabled');
      }
    }

    async handleSubmit(event) {
      const entries = Object.fromEntries(new FormData(event.target));
      this.log('Submitting form:', entries);

      if ('customer_zip' in entries) {
        const postcodeIsValid = await this.validatePostCode(entries['customer_zip']);
        if (!postcodeIsValid) {
          this.showFieldError('customer_zip');
          return {
            errorMessage: this.config.strings['customer.register.error_postcode_not_valid_html'],
            systemErrorMessage: 'Postcode is not valid'
          };
        }
      }

      const customerType = (() => {
        if (entries.customer_id) return FormWrapperRegister.customerType.EXISTING;
        return FormWrapperRegister.customerType.NEW;
      })();

      const requestBody = {
        customer: {},
        shipping_address: {},
        billing_address: {},
      };

      const route = (() => {
        if (customerType === FormWrapperRegister.customerType.EXISTING) return FormWrapperRegister.routes.CREATE_WITH_NUMBER;
        return FormWrapperRegister.routes.CREATE;
      })();

      Object.keys(entries).forEach((key) => {
        let value = String(entries[key]);

        if (key.startsWith('customer_')) {
          if (key.startsWith('customer_email')) {
            value = value.toLowerCase();
          }
          requestBody.customer[key.replace('customer_', '')] = value;
        } else if (key.startsWith('shipping_')) {
          requestBody.shipping_address[key.replace('shipping_', '')] = value;

          if ('shipping_equals_billing' in entries) {
            requestBody.billing_address[key.replace('shipping_', '')] = value;
          }
        } else if (key.startsWith('billing_')) {
          requestBody.billing_address[key.replace('billing_', '')] = value;
        }
      });

      this.log('Validating fields', requestBody);
      const { email, email_repeat: emailRepeat } = requestBody.customer;
      const emailIsValid = this.validateEmail(email);
      if (!emailIsValid) {
        this.showFieldError('customer_email');
        return { errorMessage: this.config.strings['customer.register.error_email_invalid'], systemErrorMessage: 'Email is not valid' };
      }
      if (email !== emailRepeat) {
        this.showFieldError('customer_email_repeat');
        return { errorMessage: this.config.strings['customer.register.error_email_same'], systemErrorMessage: 'Emails do not match' };
      }

      this.log('Sending Registration request for Customer Type', customerType);
      const response = await fetch(route, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        },
        body: JSON.stringify({ ...requestBody }),
      });

      if (!response.ok) {
        const { data } = await response.json();
        const [errorMessage, systemErrorMessage, fieldName] = (() => {
          switch (data) {
            case 'error.customer_not_found':
              return [this.config.strings['customer.register.error_customer_does_not_exist'], data];
            case 'error.customer_number_not_found':
              return [this.config.strings['customer.register.error_customer_number_does_not_exist'], data];
            case 'error.customer_exists':
              return [this.config.strings['customer.register.error_customer_exists'], data];
            case 'error.user_not_found':
              return [this.config.strings['customer.register.error_user_not_found'], data];
            case 'error.user_name_exists':
              return [this.config.strings['customer.register.error_user_name_exists'], data, 'customer_username'];
            case 'error.no_access_to_any_user':
              return [this.config.strings['customer.register.error_no_access_to_any_user'], 'Auth error']
            default:
              return [this.config.strings['customer.register.error'], 'Server error'];
          }
        })();

        if (fieldName) {
          this.showFieldError(fieldName);
        }

        return {
          errorMessage,
          systemErrorMessage
        };
      }

      if (customerType === FormWrapperRegister.customerType.NEW) {
        // const { key, token } = await response.json();
        // this.storeUsernameKeyAndLogin(key, token);

        // return { successMessage: this.config.strings['customer.register.success'] }

        //Due to new login system we have commented above code - 28-04-2026
        setTimeout(() => {
          window.location.href = `/customer_authentication/login?return_to=%2Fpages%2Fshop`;
        }, 1000);
      }

      /**
       * When Registering a new account with an exisiting Customer number
       * we don't log in immediately as it needs to be approved to have
       * access to that Customer Number
       */
      this.elems.submitButton.parentNode.classList.add('hidden');
      this.elems.loginContainer.classList.add('hidden');
      return { successMessage: this.config.strings['customer.register.success_existing']}
    }

    async validatePostCode(postcode) {
      if (!postcode || postcode === '') return false;

      const url = new URL(`${location.origin}/cart`);
      url.searchParams.append('view', 'validate-postcode');
      url.searchParams.append('postcode', postcode);

      const response = await fetch(url, {
        headers: {
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        }
      });
      const { isValid } = await response.json();
      return isValid;
    }

    getElems() {
      return {
        ...super.getElems(),
        customerOptions: [...this.querySelectorAll('[data-customer-option]')],
        formToggleBillingAddress: /** @type {HTMLInputElement} */ (this.querySelector('#shipping_equals_billing')),
        billingFields: /** @type {HTMLInputElement} */ (this.querySelector('.js-register-form-billing-fields')),
        loginContainer: this.querySelector('.js-register-form-login-container')
      }
    }
  }

  customElements.define('form-wrapper-register', FormWrapperRegister);
}

/**
 * PASSWORD RESET FORM
 */
if (!customElements.get('form-wrapper-password-reset')) {
  class FormWrapperPasswordReset extends CustomerForm {
    constructor() {
      super('FormWrapperPasswordReset');
    }

    async handleSubmit(event) {
      const { username } = Object.fromEntries(new FormData(event.target));

      const response = await fetch('/apps/stroetmann/api/v1/customers/password_reset_request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        },
        body: JSON.stringify({
          userName: username,
        }),
      });

      if (!response.ok) {
        const { data } = await response.json();

        const [errorMessage, systemErrorMessage] = (() => {
          switch (data) {
            case 'error.user_name_not_found':
              return [this.config.strings['customer.recover_password.error_username_not_found'], data];
            default:
              return [this.config.strings['customer.recover_password.error'], `Server error: ${JSON.stringify(data)}`];
          }
        })();

        return { errorMessage, systemErrorMessage };
      }
      this.loading = false;

      return { successMessage: this.config.strings['customer.recover_password.success'] }
    }
  }

  customElements.define('form-wrapper-password-reset', FormWrapperPasswordReset);
}

/**
 * USER RESET FORM
 */
if (!customElements.get('form-wrapper-user-reset')) {
  class FormWrapperUserReset extends CustomerForm {
    constructor() {
      super('FormWrapperUserReset');
    }

    async handleSubmit(event) {
      const { email } = Object.fromEntries(new FormData(event.target));

      const response = await fetch('/apps/stroetmann/api/v1/customers/user_reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        },
        body: JSON.stringify({
          email: String(email).toLowerCase(),
        }),
      });

      if (!response.ok) {
        const { data } = await response.json();

        const [errorMessage, systemErrorMessage] = (() => {
          switch (data) {
            case 'error.customer_not_found':
              return [this.config.strings['customer.user_reset.error_customer_does_not_exist'], data];
            default:
              return [this.config.strings['customer.user_reset.error'], `Server error: ${JSON.stringify(data)}`];
          }
        })();

        return { errorMessage, systemErrorMessage };
      }

      this.loading = false;
      return { successMessage: this.config.strings['customer.user_reset.success'] }
    }
  }

  customElements.define('form-wrapper-user-reset', FormWrapperUserReset);
}

/**
 * CHANGE PASSWORD FORM
 */
if (!customElements.get('form-wrapper-change-password')) {
  class FormWrapperChangePassword extends CustomerForm {
    constructor() {
      super('FormWrapperChangePassword');
    }

    connectedCallback() {
      super.connectedCallback();
      this.getTokenFromParameters();
    }

    async handleSubmit(event) {
      const { password, confirm_password: confirmPassword } = Object.fromEntries(new FormData(event.target));

      if (password !== confirmPassword) {
        this.showFieldError('confirm_password');
        return {
          errorMessage: 'Passwörter stimmen nicht überein',
          systemErrorMessage: 'Paswords dont match'
        };
      }

      const response = await fetch('/apps/stroetmann/api/v1/customers/password_reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        },
        body: JSON.stringify({
          token: this.token,
          password,
        }),
      });

      if (!response.ok) {
        const { data } = await response.json();

        const [errorMessage, systemErrorMessage] = (() => {
          switch (data) {
            case 'error.token_expired':
              return ['Token abgelaufen', data];
          default:
            return [this.config.strings['customer.change_password.error'], `Server error: ${JSON.stringify(data)}`];
          }
        })();

        return { errorMessage, systemErrorMessage };
      }

      this.loading = false;
      return { successMessage: this.config.strings['customer.change_password.success'] }
    }

    getTokenFromParameters() {
      const url = new URL(location.href);
      this.token = url.searchParams.get('token');
      if (!this.token) {
        //location.href = '/account/account-login';
        //Due to new login system we have commented above code - 28-04-2026
        window.location.href = `/customer_authentication/login?return_to=%2Fpages%2Fshop`;
      }
    }
  }

  customElements.define('form-wrapper-change-password', FormWrapperChangePassword);
}


/**
 * PASSWORD RESET FORM FOR ONBOARDING CUSTOMER
 */
if (!customElements.get('form-wrapper-password-reset-onboard')) {
  class FormWrapperPasswordResetOnboard extends CustomerForm {
    constructor() {
      super('FormWrapperPasswordResetOnboard');
    }

    async handleSubmit(event) {
      const { username, customerId } = Object.fromEntries(new FormData(event.target));

      const response = await fetch('/apps/stroetmann/api/v1/customers/password_reset_request_onboarding_user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Bypass-Internal': 'true',
          'No-Queue': 'true',
        },
        body: JSON.stringify({
          userName: username,
          customerId: customerId
        }),
      });

      const { data } = await response.json();

      if (!response.ok) {
        const [errorMessage, systemErrorMessage] = (() => {
          switch (data) {
            case 'error.user_name_not_found':
              return [this.config.strings['customer.recover_password.error_username_not_found'], data];
            case 'error.customer_number_not_found':
              return [this.config.strings['customer.recover_password.error_customer_number_does_not_exist'], data];
            default:
              return [this.config.strings['customer.recover_password.error'], `Server error: ${JSON.stringify(data)}`];
          }
        })();

        return { errorMessage, systemErrorMessage };
      }
      this.loading = false;

      console.log(`data >>`,data)

      //re-direct user to reset password link
      if(data.reset_link){
        window.location.href = data.reset_link;
      }

      return { successMessage: `` }
    }
  }

  customElements.define('form-wrapper-password-reset-onboard', FormWrapperPasswordResetOnboard);
}